import * as THREE from 'three'
import RAPIER from '@dimforge/rapier3d-compat'
import { SlidingDoor, createDoorInstance } from './world/doors'
import { DestructibleManager } from './world/destructibles'
import type { EnemyArchetype } from './ai/archetypes'

const WALL_HEIGHT = 4
const WALL_THICKNESS = 0.5

interface BoxSpec {
  size: [number, number, number]
  position: [number, number, number]
  color?: number
}

function addStaticBox(scene: THREE.Scene, world: RAPIER.World, spec: BoxSpec) {
  const [w, h, d] = spec.size
  const [x, y, z] = spec.position

  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshStandardMaterial({ color: spec.color ?? 0x808080 }),
  )
  mesh.position.set(x, y, z)
  scene.add(mesh)

  const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(x, y, z))
  world.createCollider(RAPIER.ColliderDesc.cuboid(w / 2, h / 2, d / 2), body)

  return mesh
}

export interface EnemySpawnConfig {
  archetype: EnemyArchetype
  position: THREE.Vector3
}

export interface Level {
  targets: THREE.Mesh[]
  doors: SlidingDoor[]
  destructibles: DestructibleManager
  enemySpawns: EnemySpawnConfig[]
}

/** Multi-room security facility with sliding blast doors, destructible crates, and shooting targets. */
export function buildLevel(scene: THREE.Scene, world: RAPIER.World): Level {
  // Main Room Floor
  addStaticBox(scene, world, { size: [24, 1, 24], position: [0, -0.5, 0], color: 0x444850 })

  // Main Room Walls
  addStaticBox(scene, world, { size: [16, WALL_HEIGHT, WALL_THICKNESS], position: [0, WALL_HEIGHT / 2, -8] })
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 16], position: [-8, WALL_HEIGHT / 2, 0] })
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 16], position: [8, WALL_HEIGHT / 2, 0] })

  // South wall with doorway
  addStaticBox(scene, world, { size: [6, WALL_HEIGHT, WALL_THICKNESS], position: [-5, WALL_HEIGHT / 2, 8] })
  addStaticBox(scene, world, { size: [6, WALL_HEIGHT, WALL_THICKNESS], position: [5, WALL_HEIGHT / 2, 8] })

  // Corridor connecting Main Room to Vault Room
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 10], position: [-2, WALL_HEIGHT / 2, 13] })
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 10], position: [2, WALL_HEIGHT / 2, 13] })
  addStaticBox(scene, world, { size: [6, 1, 12], position: [0, -0.5, 13], color: 0x333740 })

  // Security Vault Room (south of corridor)
  addStaticBox(scene, world, { size: [20, 1, 16], position: [0, -0.5, 26], color: 0x2b3038 })
  addStaticBox(scene, world, { size: [8, WALL_HEIGHT, WALL_THICKNESS], position: [-6, WALL_HEIGHT / 2, 18] })
  addStaticBox(scene, world, { size: [8, WALL_HEIGHT, WALL_THICKNESS], position: [6, WALL_HEIGHT / 2, 18] })
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 16], position: [-10, WALL_HEIGHT / 2, 26] })
  addStaticBox(scene, world, { size: [WALL_THICKNESS, WALL_HEIGHT, 16], position: [10, WALL_HEIGHT / 2, 26] })
  addStaticBox(scene, world, { size: [20, WALL_HEIGHT, WALL_THICKNESS], position: [0, WALL_HEIGHT / 2, 34] })

  // Cover boxes in the main room
  addStaticBox(scene, world, { size: [1.5, 1.5, 1.5], position: [-3, 0.75, -3], color: 0x5a606a })
  addStaticBox(scene, world, { size: [1.5, 1.5, 1.5], position: [4, 1, 2], color: 0x5a606a })

  // Sliding Doors
  const doors: SlidingDoor[] = []
  // Door 1: Corridor blast door (unlocked)
  doors.push(
    createDoorInstance(scene, world, RAPIER, {
      id: 'door-main-corridor',
      position: new THREE.Vector3(0, 0, 8),
      width: 4.0,
      height: WALL_HEIGHT,
      slideDistance: 2.0,
    }),
  )

  // Door 2: Vault Security blast door (requires RED keycard)
  doors.push(
    createDoorInstance(scene, world, RAPIER, {
      id: 'door-vault-red',
      position: new THREE.Vector3(0, 0, 18),
      width: 4.0,
      height: WALL_HEIGHT,
      slideDistance: 2.0,
      requiredKeycard: 'red',
    }),
  )

  // Destructible Crates
  const destructibles = new DestructibleManager(scene)
  destructibles.createCrate(
    { id: 'crate-spread', position: new THREE.Vector3(-5, 0.6, -3), size: [1.2, 1.2, 1.2], lootDrop: 'spread-ammo' },
    world,
    RAPIER,
  )
  destructibles.createCrate(
    { id: 'crate-keycard-red', position: new THREE.Vector3(5, 0.6, 3), size: [1.2, 1.2, 1.2], lootDrop: 'keycard-red' },
    world,
    RAPIER,
  )
  destructibles.createCrate(
    { id: 'crate-missile', position: new THREE.Vector3(0, 0.6, 14), size: [1.2, 1.2, 1.2], lootDrop: 'missile-ammo' },
    world,
    RAPIER,
  )

  const enemySpawns: EnemySpawnConfig[] = [
    { archetype: 'scout-drone', position: new THREE.Vector3(-4, 2.5, -4) },
    { archetype: 'cyborg-trooper', position: new THREE.Vector3(3, 0.9, -2) },
    { archetype: 'riot-trooper', position: new THREE.Vector3(0, 0.9, 13) },
    { archetype: 'defense-turret', position: new THREE.Vector3(0, 2.5, 30) },
    { archetype: 'cyborg-trooper', position: new THREE.Vector3(4, 0.9, 26) },
  ]

  const targetPositions: [number, number, number][] = [
    [-3, 1.5, -7],
    [3, 1.7, -6],
    [0, 1.6, 28],
  ]

  const targets = targetPositions.map((position) => {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.8, 0.8),
      new THREE.MeshStandardMaterial({ color: 0xff6a00 }),
    )
    mesh.position.set(...position)
    scene.add(mesh)
    return mesh
  })

  return { targets, doors, destructibles, enemySpawns }
}
